import { useCallback, useState } from 'react';
import { requestChatTimeRule } from 'utils/pendingChatTimeRule';
import type { TimeRuleDetails, TimeRuleMode, TimeRuleStepKind, TimeRuleType } from './types';

// Opens the Settings drawer straight onto Time Rules. 'Custom Rules' is the
// panel key SettingsBody switches on; the sidebar label reads "Time Rules".
const TIME_RULES_PATH = '/?openDrawer=true&drawerSelection=Custom Rules';

interface Params {
	appendMessage: (msg: { text: string; timeRuleStep?: TimeRuleStepKind }) => void;
	setActiveStep: (step: TimeRuleStepKind) => void;
	goToTimeRules: (path: string) => void;
}

/**
 * Drives the Time Rule handoff as chat cards. Same shape as
 * MappingRuleChat/useMappingRuleChat — collect a little, then defer to the real
 * form — with one extra field: the rule type.
 */
export function useTimeRuleChat({ appendMessage, setActiveStep, goToTimeRules }: Params) {
	const [step, setStep] = useState<TimeRuleStepKind | null>(null);
	const [mode, setMode] = useState<TimeRuleMode>('create');
	const [details, setDetails] = useState<TimeRuleDetails>({ name: '', description: '', ruleType: 'split' });
	const [error, setError] = useState<string | null>(null);

	const open = useCallback(
		(openMode: TimeRuleMode = 'create', ruleId?: string, ruleType?: TimeRuleType) => {
			setMode(openMode);
			setError(null);
			// Seeded from whatever the message said; split is the most common and
			// is what the modal itself defaults to.
			setDetails({ name: '', description: '', ruleType: ruleType || 'split' });

			if (openMode === 'edit' && ruleId) {
				requestChatTimeRule({ ruleId });
				setStep('handed_off');
				appendMessage({ text: '', timeRuleStep: 'handed_off' });
				goToTimeRules(TIME_RULES_PATH);
				return;
			}

			setStep('details');
			appendMessage({ text: '', timeRuleStep: 'details' });
		},
		[appendMessage, goToTimeRules]
	);

	const patchDetails = useCallback((patch: Partial<TimeRuleDetails>) => {
		setDetails((prev) => ({ ...prev, ...patch }));
		setError(null);
	}, []);

	const handOff = useCallback(() => {
		const name = details.name.trim();
		if (!name) {
			setError('Give the rule a name first.');
			return;
		}
		requestChatTimeRule({ name, description: details.description.trim(), ruleType: details.ruleType });
		setStep('handed_off');
		setActiveStep('handed_off');
		goToTimeRules(TIME_RULES_PATH);
	}, [details, setActiveStep, goToTimeRules]);

	return { step, mode, details, error, open, patchDetails, handOff };
}

export type TimeRuleChat = ReturnType<typeof useTimeRuleChat>;
